import template from './uc-config-page.html.twig';
import './uc-config-page.scss';

const { Mixin } = Shopware;

const DEFAULT_ELEMENT_ORDER = '["trust-bar","usp-icons","timer","progress-bar","gift-offers","line-items","cross-sell","discount","savings-banner","order-summary","custom-html","custom-html-2","custom-html-3"]';

Shopware.Component.register('uc-config-page', {
    template,

    inject: ['systemConfigApiService'],

    mixins: [
        Mixin.getByName('notification'),
    ],

    data() {
        return {
            salesChannelId: null,
            isSaving: false,

            // The "All Sales Channels" (global) row — always a concrete
            // value, and the fallback shown/used whenever a specific
            // channel doesn't have its own override.
            globalTheme: 'classic',
            globalElementOrder: DEFAULT_ELEMENT_ORDER,
            globalDisabledElements: '[]',

            // Raw override values for the CURRENTLY selected sales channel
            // only — null means "no override row exists, this channel is
            // inheriting the global value". Always null while
            // salesChannelId === null (the global row has no parent to
            // inherit from). See loadCustomConfig() for how these are
            // fetched WITHOUT the merge/fallback Shopware's own inherit
            // flag would otherwise apply.
            channelTheme: null,
            channelElementOrder: null,
            channelDisabledElements: null,
        };
    },

    computed: {
        isSalesChannelScoped() {
            return this.salesChannelId !== null;
        },

        themeInherited() {
            return this.isSalesChannelScoped && this.channelTheme === null;
        },

        // Order + visibility are edited together in one card via
        // uc-element-sorter, so they're treated as a single inherit/
        // override unit rather than two independently-inherited fields.
        elementsInherited() {
            return this.isSalesChannelScoped
                && this.channelElementOrder === null
                && this.channelDisabledElements === null;
        },

        currentTheme() {
            if (!this.isSalesChannelScoped) return this.globalTheme;
            return this.themeInherited ? this.globalTheme : this.channelTheme;
        },

        elementOrder() {
            if (!this.isSalesChannelScoped) return this.globalElementOrder;
            return this.elementsInherited ? this.globalElementOrder : (this.channelElementOrder ?? this.globalElementOrder);
        },

        disabledElements() {
            if (!this.isSalesChannelScoped) return this.globalDisabledElements;
            return this.elementsInherited ? this.globalDisabledElements : (this.channelDisabledElements ?? this.globalDisabledElements);
        },
    },

    created() {
        this.loadCustomConfig();
    },

    methods: {
        async loadCustomConfig() {
            try {
                // Global values are always needed — they're both what gets
                // edited on "All Sales Channels" AND the fallback displayed
                // for any channel that's still inheriting.
                const globalValues = await this.systemConfigApiService.getValues('SellopsUltimateCart.config', null);
                this.globalTheme = globalValues['SellopsUltimateCart.config.theme'] ?? 'classic';
                this.globalElementOrder = globalValues['SellopsUltimateCart.config.elementOrder'] ?? DEFAULT_ELEMENT_ORDER;
                this.globalDisabledElements = globalValues['SellopsUltimateCart.config.disabledElements'] ?? '[]';

                if (this.isSalesChannelScoped) {
                    // getValues() with no extra `inherit` param returns ONLY
                    // the raw rows explicitly stored for this exact
                    // salesChannelId — no fallback merge (that's an opt-in
                    // Shopware only applies via an `inherit: true` query
                    // param we deliberately don't pass here). A key that's
                    // missing from the response means "no override saved
                    // for this channel", i.e. still inheriting global.
                    const channelValues = await this.systemConfigApiService.getValues('SellopsUltimateCart.config', this.salesChannelId);
                    this.channelTheme = channelValues['SellopsUltimateCart.config.theme'] ?? null;
                    this.channelElementOrder = channelValues['SellopsUltimateCart.config.elementOrder'] ?? null;
                    this.channelDisabledElements = channelValues['SellopsUltimateCart.config.disabledElements'] ?? null;
                } else {
                    this.channelTheme = null;
                    this.channelElementOrder = null;
                    this.channelDisabledElements = null;
                }
            } catch {
                // Defaults already set in data()
            }
        },

        // Single Save button — saves all sw-system-config fields FIRST, then
        // our custom-tracked fields (theme + elementOrder + disabledElements)
        // LAST so they always win. These three are NOT in config.xml, so
        // sw-system-config never touches them — but saving our values last
        // is an extra safety net.
        async onSave() {
            this.isSaving = true;
            let hasError = false;

            // 1. Save sw-system-config fields first
            if (this.$refs.systemConfig) {
                try {
                    if (typeof this.$refs.systemConfig.save === 'function') {
                        await this.$refs.systemConfig.save();
                    } else if (typeof this.$refs.systemConfig.saveAll === 'function') {
                        await this.$refs.systemConfig.saveAll();
                    }
                } catch (e) {
                    console.error('[UltimateCart] Failed to save system config fields:', e);
                    hasError = true;
                }
            }

            // 2. Save custom-tracked fields last — always wins. On the
            // global row there's no inheritance concept, so it's always a
            // concrete write. On a specific sales channel, a field that's
            // still inherited is saved as `null` — SystemConfigService
            // deletes that channel's row for the key on `null`, which is
            // exactly what restores/keeps it following the global value on
            // future reads (same mechanism Shopware's own inherit-wrapper
            // uses for "Reset to global value").
            try {
                if (!this.isSalesChannelScoped) {
                    await this.systemConfigApiService.saveValues({
                        'SellopsUltimateCart.config.theme':            this.globalTheme,
                        'SellopsUltimateCart.config.elementOrder':     this.globalElementOrder,
                        'SellopsUltimateCart.config.disabledElements': this.globalDisabledElements,
                    }, null);
                } else {
                    await this.systemConfigApiService.saveValues({
                        'SellopsUltimateCart.config.theme':            this.themeInherited ? null : this.channelTheme,
                        'SellopsUltimateCart.config.elementOrder':     this.elementsInherited ? null : this.channelElementOrder,
                        'SellopsUltimateCart.config.disabledElements': this.elementsInherited ? null : this.channelDisabledElements,
                    }, this.salesChannelId);
                }
            } catch (e) {
                console.error('[UltimateCart] Failed to save custom config:', e);
                hasError = true;
            }

            this.isSaving = false;

            if (hasError) {
                this.createNotificationError({
                    message: this.$tc('sellops-ultimate-cart.config.saveError'),
                });
            } else {
                this.createNotificationSuccess({
                    message: this.$tc('sellops-ultimate-cart.config.saveSuccess'),
                });
            }

            // Re-fetch so channelTheme/channelElementOrder/channelDisabled
            // Elements reflect what's actually stored now (e.g. after a
            // "Reset to global value" save, these go back to null).
            await this.loadCustomConfig();
        },

        onThemeChange(value) {
            if (!this.isSalesChannelScoped) {
                this.globalTheme = value;
            } else {
                this.channelTheme = value;
            }
        },

        onElementOrderChange(value) {
            if (!this.isSalesChannelScoped) {
                this.globalElementOrder = value;
                return;
            }
            this.channelElementOrder = value;
            // Editing just the order still counts as "customizing this
            // group" even if disabledElements hasn't been touched yet —
            // seed it with the current effective value so a save right
            // after doesn't accidentally null out the other half.
            if (this.channelDisabledElements === null) {
                this.channelDisabledElements = this.disabledElements;
            }
        },

        onDisabledElementsChange(value) {
            if (!this.isSalesChannelScoped) {
                this.globalDisabledElements = value;
                return;
            }
            this.channelDisabledElements = value;
            if (this.channelElementOrder === null) {
                this.channelElementOrder = this.elementOrder;
            }
        },

        // Un-inherit: seed the channel override with whatever's currently
        // effectively showing (the global value, since these are only
        // callable while inherited) so editing starts from that instead of
        // a blank slate.
        customizeTheme() {
            this.channelTheme = this.currentTheme;
        },

        resetThemeToGlobal() {
            this.channelTheme = null;
        },

        customizeElements() {
            this.channelElementOrder = this.elementOrder;
            this.channelDisabledElements = this.disabledElements;
        },

        resetElementsToGlobal() {
            this.channelElementOrder = null;
            this.channelDisabledElements = null;
        },

        onSystemConfigChanged() {
            // sw-system-config tracks its own field state internally
        },

        // Keep salesChannelId in sync so the milestone manager re-filters on switch
        onSalesChannelChanged(salesChannelId) {
            this.salesChannelId = salesChannelId ?? null;
            this.loadCustomConfig();
        },

        goToMilestones() {
            this.$router.push({ name: 'sellops.ultimate.cart.milestones' });
        },

        goToFreeProducts() {
            this.$router.push({ name: 'sellops.ultimate.cart.freeproducts' });
        },

        goToCrossSellRules() {
            this.$router.push({ name: 'sellops.ultimate.cart.crosssellrules' });
        },
    },
});
